import type { Category } from "./category";
import type { OptionType } from "./generated/productOptionType";

import type { CommonRes, PublicLanguageCode } from ".";
import { api } from ".";

interface Banner {
  banner: string;
  mobileBanner: string | null;
  url: string | null;
}

interface GetProductBannerRes {
  total: number;
  list: Array<Banner>;
}

export const getProductBanner = () =>
  api.get("product/banner").json<CommonRes<GetProductBannerRes>>();

interface ProductCategory extends Category {
  image: string;
}

interface GetProductCategoryReq extends PublicLanguageCode {
  categoryId?: number;
}

interface GetProductCategoryRes {
  total: number;
  list: Array<ProductCategory>;
}

export const getProductCategory = ({
  categoryId,
  languageCode,
}: GetProductCategoryReq) =>
  api
    .get("product/category", {
      searchParams: {
        categoryId,
        languageCode,
      },
    })
    .json<CommonRes<GetProductCategoryRes>>();

export interface GetProductListReq extends PublicLanguageCode {
  page: number;
  count: number;
  search?: string;
  brandId?: number;
  categoryId?: number;
  productCategoryId?: number;
  optionIdList?: number[];
  sortColumn?: string; // creatDate, price
  sort?: string; // ASC, DESC
  mainView?: boolean;
}

export interface ProductItem {
  id: number;
  brandName: string;
  productName: string;
  price: number;
  like: number;
  review: number;
  reviewAverage: number;
  image: string;
  colorName: string;
  colorCode: string;
  isLiked: boolean;
}

export interface External {
  id: number;
  name: string;
  imageUrl: string;
  url: string;
}

export interface GetProductListRes {
  total: number;
  list: Array<ProductItem>;
}

export const getProductList = ({
  optionIdList,
  mainView,
  ...params
}: GetProductListReq) => {
  const searchParams: Array<Array<string | number | boolean>> = Object.entries(
    params,
  ).reduce<Array<Array<string | number | boolean>>>(
    (acc, [key, value]) =>
      value === undefined || value === null ? acc : [...acc, [key, value]],
    [],
  );

  if (Array.isArray(optionIdList)) {
    optionIdList.forEach((id) => {
      searchParams.push(["optionIdList", id]);
    });
  }

  if (mainView != null) {
    searchParams.push(["mainView", mainView]);
  }

  return api
    .get("product", {
      searchParams,
    })
    .json<CommonRes<GetProductListRes>>();
};

interface GetProductDetailReq extends PublicLanguageCode {
  id: number;
}

export interface OptionValue {
  id: number;
  value: string;
}

// 옵션 축 타입의 원본은 생성 파일이지만, 이 축을 쓰는 쪽은 응답 타입(`DetailOption`)과
// 늘 같이 필요하다. 상위 레이어가 두 곳을 import하지 않도록 서비스 모듈에서 함께 공개한다.
export type { OptionType };

// 상세 응답은 해당 상품이 가진 옵션 키만 내려준다 (전체 OptionType 중 일부).
export type DetailOption = Partial<Record<OptionType, OptionValue[]>>;

export interface ProductDetailBrand {
  id: number;
  name: string;
  profileImg: string;
}

/** 옵션 조합 단위의 실판매 단위(SKU). 장바구니는 이 `id` 로 담는다. */
export interface ProductVariant {
  id: number;
  sku: string;
  /** 이 조합을 이루는 옵션값 ID 목록. `DetailOption` 의 `OptionValue.id` 와 맞물린다 */
  optionValueIds: number[];
  stockQuantity: number;
  /** 재고가 없거나 판매 중지된 조합이면 true */
  isSoldOut: boolean;
}

export interface GetProductDetailRes {
  id: number;
  name: string;
  brand: ProductDetailBrand;
  price: number;
  discountPrice: number;
  origin: string;
  /** 출고까지 걸리는 일수 */
  shippingInfo: number;
  /** 옵션 축별 값. 실제 구매 가능한 조합은 `variants` 를 봐야 한다 */
  option: DetailOption;
  variants: ProductVariant[];
  like: number;
  review: number;
  reviewAverage: number;
  detailImg: string;
  subImage: string[];
  relate: ProductItem[];
  external: External[];
  isLiked: boolean;
}

/**
 * @description 상품 상세 조회 (v1)
 *
 * v0(`product/{id}`) 대비 `variants`(옵션 조합 SKU·재고)가 추가되고 `shippingCost` 가 빠졌다.
 * 배송비는 상품이 아니라 배송지로 정해지므로 `GET shipping-policy` 를 쓴다.
 */
export const getProductDetail = ({ id, languageCode }: GetProductDetailReq) =>
  api
    .get(`product/v1/${id}`, {
      searchParams: {
        languageCode,
      },
    })
    .json<CommonRes<GetProductDetailRes>>();

export interface GetProductBrandBannerReq extends PublicLanguageCode {
  brandId: number;
}

export interface GetProductBrandBannerRes {
  brandId: number;
  banner: string;
  name: string;
  englishName: string;
  description: string;
  like: number;
  isLiked: boolean;
}

export const getProductBrandBanner = ({
  brandId,
  languageCode,
}: GetProductBrandBannerReq) =>
  api
    .get(`product/banner/brand`, {
      searchParams: {
        brandId,
        languageCode,
      },
    })
    .json<CommonRes<GetProductBrandBannerRes>>();

export type SortOption = Category & {
  sortColumn: string;
  sort: "ASC" | "DESC";
};

interface GetProductSortFilterRes {
  total: number;
  list: Array<SortOption>;
}

export const getProductSortFilter = ({ languageCode }: PublicLanguageCode) =>
  api
    .get("product/sort/filter", {
      searchParams: {
        languageCode,
      },
    })
    .json<CommonRes<GetProductSortFilterRes>>();

interface Option {
  id: number;
  type: string;
}

interface GetProductOptionsRes {
  total: number;
  list: Array<Option>;
}

// 상품 옵션 리스트
export const getProductOptions = () =>
  api.get("product/option").json<CommonRes<GetProductOptionsRes>>();

interface GetProductOptionValueReq extends PublicLanguageCode {
  optionId: number;
}

interface GetProductOptionValuRes {
  total: number;
  list: Array<OptionValue>;
}

// 상품 옵션 값 리스트
export const getProdctionOptionValue = ({
  optionId,
  languageCode,
}: GetProductOptionValueReq) =>
  api
    .get("product/option/value", {
      searchParams: {
        optionId,
        languageCode,
      },
    })
    .json<CommonRes<GetProductOptionValuRes>>();

export interface ProductFilter {
  title: string;
  optionValueList: Array<ProductFilterOptionValue>;
  type: "RADIO" | "GRID";
}

export interface GridOptionInfo extends ProductFilter {
  type: "GRID";
}

export interface RadioOptionInfo extends ProductFilter {
  type: "RADIO";
}

export type OptionInfo = GridOptionInfo | RadioOptionInfo;

interface ProductFilterOptionValue {
  optionId: number;
  value: string;
  colorCode?: string;
}

interface GetProductFilterReq extends PublicLanguageCode {
  categoryId: number;
  brandId?: number;
  productCategoryId?: number;
}

interface GetProductFilterRes {
  total: number;
  list: Array<ProductFilter>;
}

export const getProductFilter = ({
  categoryId,
  brandId,
  productCategoryId,
  languageCode,
}: GetProductFilterReq) =>
  api
    .get("product/filter", {
      searchParams: {
        categoryId,
        brandId,
        productCategoryId,
        languageCode,
      },
    })
    .json<CommonRes<GetProductFilterRes>>();
